/**
 * bulkAction/BulkAssignModal.jsx
 *
 * Assign selected KPI questions to users.
 *
 * managementListValue is fetched in QuantativeList via getMasterData (same as
 * Qualitative.jsx) and passed down as a prop — we never fetch it here.
 *
 * Props:
 *   show                {boolean}
 *   onHide              {function}
 *   selectedIds         {number[]}  — checked KPI question IDs
 *   financialYearId     {number}
 *   managementListValue {array}     — [{id, role_name, ...}] from getMasterData
 *   onSuccess           {function}  — called after successful save
 */
import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, FieldLabel, StyledSelect, Chip, Spin } from "./Shared";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const BulkAssignModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYearId,
  managementListValue = [],
  onSuccess,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  /* user pool — grows as roles are picked (multi-role support) */
  const [userPool, setUserPool] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]); // [{id,name}]
  const [selectedUsers, setSelectedUsers] = useState([]); // [{id,name}]
  const [selfAssign, setSelfAssign] = useState(false);
  const [dueDate, setDueDate] = useState(todayStr());
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* ── reset every time modal opens ── */
  useEffect(() => {
    if (!show) return;
    setUserPool([]);
    setSelectedUsers([]);
    setSelfAssign(false);
    setDueDate(todayStr());
    setError("");

    if (managementListValue && managementListValue.length > 0) {
      const defaultRole = managementListValue.find((r) => String(r.id) === "6" || r.role_name?.toLowerCase() === "data owner") || managementListValue[0];
      const id = String(defaultRole.id);
      const name = defaultRole.role_name || defaultRole.name || "Data Owner";
      setSelectedRoles([{ id, name }]);
      fetchUsersForRole(id);
    } else {
      setSelectedRoles([]);
    }
  }, [show, managementListValue]);

  /* ── fetch users for a role and merge into pool ── */
  const fetchUsersForRole = async (roleId) => {
    setLoadingUsers(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUserBasedOnRoleId`,
        {},
        { roleId: String(roleId) },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const incoming = (data?.data || []).map((u) => ({
          id: String(u.userId),
          name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
            (u.designation ? ` (${u.designation})` : ""),
        }));
        setUserPool((prev) => {
          const existingIds = new Set(prev.map((x) => x.id));
          return [...prev, ...incoming.filter((x) => !existingIds.has(x.id))];
        });
      }
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  /* ── when user picks a role from the dropdown ── */
  const handleRoleAdd = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = ""; // reset select to placeholder
    if (selectedRoles.some((r) => r.id === id)) return; // already added
    const role = managementListValue.find((r) => String(r.id) === id);
    const name = role?.role_name || role?.name || `Role ${id}`;
    setSelectedRoles((prev) => [...prev, { id, name }]);
    fetchUsersForRole(id);
  };

  const handleRoleRemove = (id) => {
    setSelectedRoles((prev) => prev.filter((r) => r.id !== id));
    // also remove any selected users that came from this role
    // (we can't easily track, so just leave — user can deselect manually)
  };

  /* ── when user picks a user from dropdown ── */
  const handleUserAdd = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    if (selectedUsers.some((u) => u.id === id)) return;
    const u = userPool.find((x) => x.id === id);
    if (u) setSelectedUsers((prev) => [...prev, u]);
  };

  /* ── save ── */
  const handleSave = async () => {
    setError("");
    let assignedToIds;

    if (selfAssign) {
      const me = JSON.parse(localStorage.getItem("currentUser") || "{}");
      if (!me?.id) {
        setError("Could not read current user. Please re-login.");
        return;
      }
      assignedToIds = [String(me.id)];
    } else {
      if (!selectedUsers.length) {
        setError("Please select at least one user to assign to.");
        return;
      }
      assignedToIds = selectedUsers.map((u) => u.id);
    }

    if (!dueDate) {
      setError("Please select a due date.");
      return;
    }

    setSaving(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}assignedQuestionToUser`,
        {},
        {
          financialYearId,
          questionIds: selectedIds,
          assignedToIds,
          dueDate,
          moduleType: "SQ",
          questionnaireType: "CA",
          type: "ASSIGN",
        },
        "POST"
      );
      if (isSuccess) {
        onSuccess?.();
        onHide();
      } else {
        setError("Assignment failed. Please try again.");
      }
    } catch {
      setError("Server error. Please try again.");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 480 }}>
        {/* Header */}
        <div style={S.header}>
          <span style={S.title}>Assign Question</span>
          <button onClick={onHide} disabled={saving} style={S.closeBtn}>✕</button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {error && <div style={S.errBox}>{error}</div>}

          <div style={S.infoBox}>
            <b>{selectedIds.length}</b> KPI
            {selectedIds.length !== 1 ? "s" : ""} selected for assignment
          </div>

          {/* Self Assign toggle */}
          <label style={S.checkRow}>
            <input
              type="checkbox"
              checked={selfAssign}
              onChange={(e) => setSelfAssign(e.target.checked)}
            />
            <span style={{ fontSize: 13, color: "#334155" }}>Self Assign</span>
          </label>

          {!selfAssign && (
            <>
              {/* User picker */}
              <div style={S.field}>
                <FieldLabel>
                  Select User
                  {loadingUsers && <Spin />}
                </FieldLabel>
                <StyledSelect
                  value=""
                  onChange={handleUserAdd}
                  disabled={!selectedRoles.length || loadingUsers}
                >
                  <option value="">
                    {!selectedRoles.length
                      ? "Select a role first"
                      : loadingUsers
                        ? "Loading users…"
                        : "— Select a User —"}
                  </option>
                  {userPool.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </StyledSelect>

                {selectedUsers.length > 0 && (
                  <div style={S.chipRow}>
                    {selectedUsers.map((u) => (
                      <Chip
                        key={u.id}
                        label={u.name}
                        onRemove={() =>
                          setSelectedUsers((p) => p.filter((x) => x.id !== u.id))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Due Date */}
          {/* <div style={S.field}>
            <FieldLabel>Due Date</FieldLabel>
            <input
              type="date"
              value={dueDate}
              min={todayStr()}
              onChange={(e) => setDueDate(e.target.value)}
              style={S.dateInput}
            />
          </div> */}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button onClick={onHide} disabled={saving} style={S.btnSecondary}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...S.btnPrimary, opacity: saving ? 0.75 : 1 }}
          >
            {saving ? (
              <>
                <Spin light size={12} /> Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;